import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { HandCoins, ShieldCheck, Send } from "lucide-react";

const services = [
  {
    id: "retraite",
    icon: HandCoins,
    title: "Audit de retraite",
    lede: "Constitution de dossier et accompagnement complet",
    bullets: [
      "Analyse et vérification de vos relevés de carrière (RIS, RGCU)",
      "Reconstitution des trimestres manquants et régularisations",
      "Estimation personnalisée du montant de votre pension",
      "Constitution complète du dossier de départ à la retraite",
      "Accompagnement jusqu'à la liquidation effective",
    ],
  },
  {
    id: "assurance-pret",
    icon: ShieldCheck,
    title: "Négociation d'assurance de prêt immobilier",
    lede: "Optimisation dans le cadre de la loi Lemoine",
    bullets: [
      "Étude comparative de votre assurance emprunteur actuelle",
      "Recherche des meilleures garanties au meilleur tarif",
      "Substitution possible à tout moment (loi Lemoine 2022)",
      "Gestion complète du dossier de résiliation et de mise en place",
      "Économies pouvant atteindre plusieurs milliers d'euros sur la durée du prêt",
    ],
  },
];

const PersonalizedServicesSection = () => {
  const [service, setService] = useState("Audit de retraite");
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const change = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.length < 2 || !form.email || form.message.length < 5) {
      toast.error("Merci de remplir tous les champs obligatoires.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/contact", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone || null,
        subject: `[Services personnalisés] ${service}`,
        message: `Demande concernant : ${service}\n\n${form.message}`,
      });
      if (data.warning) toast.warning(data.warning);
      else toast.success("Votre demande a bien été envoyée. Nous revenons vers vous sous 24h.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Envoi impossible pour le moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="services-personnalises" className="py-24 md:py-32 bg-white" data-testid="personalized-services-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="reveal max-w-3xl">
          <p className="eyebrow text-teal-accent">Services personnalisés</p>
          <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-[1.05] text-teal-dark">
            Au-delà de la comptabilité, un accompagnement patrimonial.
          </h2>
          <p className="mt-6 text-ink-500 leading-relaxed">
            Deux missions dédiées aux particuliers et dirigeants qui souhaitent sécuriser leur
            avenir personnel et optimiser leurs engagements financiers.
          </p>
        </div>

        <div className="mt-16 grid lg:grid-cols-12 gap-10">
          {/* SERVICES LIST */}
          <div className="lg:col-span-7 space-y-px">
            {services.map(({ id, icon: Icon, title, lede, bullets }) => (
              <div
                key={id}
                data-testid={`perso-service-${id}`}
                className="reveal border-t border-ink-200 pt-10 pb-2"
              >
                <div className="flex items-start gap-5">
                  <div className="bg-teal-l1 p-3 shrink-0">
                    <Icon size={22} className="text-teal-dark" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-serif text-3xl text-teal-dark leading-tight">{title}</h3>
                    <p className="mt-2 text-teal-accent eyebrow">{lede}</p>
                    <ul className="mt-6 space-y-3">
                      {bullets.map((b) => (
                        <li key={b} className="flex gap-3 items-start text-ink-800 text-sm">
                          <span className="mt-2 w-1.5 h-1.5 bg-teal-accent shrink-0" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* CONTACT FORM */}
          <div className="lg:col-span-5 reveal">
            <form
              onSubmit={submit}
              data-testid="perso-form"
              className="bg-teal-dark text-white p-8 md:p-10 grain relative overflow-hidden"
            >
              <p className="eyebrow text-teal-l3">Demande d'information</p>
              <h3 className="mt-3 font-serif text-3xl leading-tight">Parlez-nous de votre situation.</h3>
              <p className="mt-3 text-sm text-teal-l3">
                Nous vous recontactons sous 24h ouvrées, en toute confidentialité.
              </p>

              <div className="mt-8 space-y-4">
                <div>
                  <label className="eyebrow text-teal-l3 block mb-2 text-xs">Service concerné *</label>
                  <select
                    data-testid="perso-service-select"
                    value={service}
                    onChange={(e) => setService(e.target.value)}
                    className="w-full bg-teal-mid border border-white/20 px-4 py-3 text-white focus:border-teal-l3 focus:outline-none"
                  >
                    {services.map((s) => <option key={s.id} value={s.title}>{s.title}</option>)}
                  </select>
                </div>

                <div>
                  <label className="eyebrow text-teal-l3 block mb-2 text-xs">Nom complet *</label>
                  <input
                    data-testid="perso-name"
                    required minLength={2}
                    value={form.name} onChange={change("name")}
                    className="w-full bg-teal-mid border border-white/20 px-4 py-3 text-white focus:border-teal-l3 focus:outline-none placeholder:text-teal-l3/60"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="eyebrow text-teal-l3 block mb-2 text-xs">Email *</label>
                    <input
                      data-testid="perso-email"
                      type="email" required
                      value={form.email} onChange={change("email")}
                      className="w-full bg-teal-mid border border-white/20 px-4 py-3 text-white focus:border-teal-l3 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="eyebrow text-teal-l3 block mb-2 text-xs">Téléphone</label>
                    <input
                      data-testid="perso-phone"
                      value={form.phone} onChange={change("phone")}
                      className="w-full bg-teal-mid border border-white/20 px-4 py-3 text-white focus:border-teal-l3 focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="eyebrow text-teal-l3 block mb-2 text-xs">Votre message *</label>
                  <textarea
                    data-testid="perso-message"
                    required minLength={5} rows={4}
                    placeholder="Décrivez brièvement votre situation ou vos questions…"
                    value={form.message} onChange={change("message")}
                    className="w-full bg-teal-mid border border-white/20 px-4 py-3 text-white focus:border-teal-l3 focus:outline-none resize-none placeholder:text-teal-l3/60"
                  />
                </div>
              </div>

              <button
                type="submit"
                data-testid="perso-submit"
                disabled={loading}
                className="mt-6 inline-flex items-center gap-2 bg-white text-teal-dark px-6 py-3 hover:bg-teal-l1 transition-colors w-full justify-center disabled:opacity-60"
              >
                {loading ? "Envoi en cours…" : (<>Envoyer ma demande <Send size={16} /></>)}
              </button>

              <p className="mt-4 text-xs text-teal-l3">
                Vos informations restent confidentielles et ne sont utilisées que pour vous répondre.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PersonalizedServicesSection;
